import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Container,
  Typography,
  IconButton,
  CardHeader,
  CardContent,
  Box,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid"; 
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { fDateTime } from "../utils/formatTime";

import {
  getAccessToken,
  handleAuthenticationFailure,
} from "../AuthenticationData.ts";
import FitAnalyzerDialog from "./FitAnalyzerDialog";

const AnalysisHistoryPage = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  useEffect(() => {
    let alive = true;
    const fetchHistory = async () => {
      setLoading(true);
      const token = getAccessToken();
      if (!token) { handleAuthenticationFailure(navigate, toast); return; }
      try {
        const res = await axios.get("/api/analyzer/my-history", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setHistory(res?.data || []);
      } catch (error) {
        console.error("Failed to fetch history", error);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchHistory();
    return () => { alive = false; };
  }, [navigate]);

  const handleViewDetails = async (row) => {
    const token = getAccessToken();
    try {
      const response = await axios.get(`/api/analyzer/history-detail/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const { report, fullDescription } = response.data;
      const reportData = typeof report === 'string' ? JSON.parse(report) : report;
      
      setSelectedReport({
        ...reportData,
        rawJobDescription: fullDescription,
        fullJobTitle: row.jobTitle
      });
      
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch detail", error);
      toast.error("Failed to open report.");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "success";
    if (score >= 50) return "warning";
    return "error";
  };

  const columns = [
    {
      field: "jobTitle",
      headerName: "Analyzed Role",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ ml: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            {params.value || "Unknown Role"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "matchScore",
      headerName: "Match",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={`${params.value}%`}
          color={getScoreColor(params.value)}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? fDateTime(params.value) : "—"}
        </Typography>
      )
    },
    {
      field: "action",
      headerName: "Action",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: () => (
        <IconButton color="primary" size="small">
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        AI Analysis History
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Assessments Directory"
              subheader="Click a row to re-open the full AI strategic fit-gap report."
            />
            
            <CardContent>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={history}
                  columns={columns}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[5]}
                  disableRowSelectionOnClick
                  onRowClick={(params) => handleViewDetails(params.row)}
                  getRowId={(row) => row.id}
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "rgba(145, 158, 171, 0.12)",
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px dashed rgba(145, 158, 171, 0.2)",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(145, 158, 171, 0.08)",
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4} />
      </Grid>

      {selectedReport && (
        <FitAnalyzerDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          readOnlyData={selectedReport}
        />
      )}
    </Container>
  );
};

export default AnalysisHistoryPage;